import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'material-react-toastify';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { createTag, deleteTag, getTag, updateTag } from '../../api/Tag';
import { getEmptyTag } from '../../interfaces/Tag/Tag';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';

export const ModifyTagPage = () => {
  const navigate = useNavigate();

  const { tagID: tagIDAsStr } = useParams<string>();

  const tagID = tagIDAsStr ? Number(tagIDAsStr) : undefined;
  const isEditing = Number.isFinite(tagID);

  const [tag, setTag] = useState(getEmptyTag());
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshTag = () => {
    setLoading(true);
    getTag(tagID!)
      .then((t) => setTag(t))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshTag();
    }
  }, [tagIDAsStr]);

  const handleSave = () => {
    if (isEditing) {
      setLoading(true);
      updateTag(tag)
        .then((updatedTag) => {
          toast.success('Etiket başarıyla güncellendi');
          setTag(updatedTag);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createTag(tag)
        .then((createdTag) => {
          toast.success('Etiket başarıyla oluşturuldu');
          navigate(`/products/tags/${createdTag.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteTag(tag.id)
      .then(() => {
        toast.success('Etiket başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/products/tags');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili etiketi silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Etiketi Düzenle' : 'Yeni Etiket'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 400,
          height: 'fit-content',
          p: 3,
          gap: 1.5,
        }}
      >
        <TextField
          label='İsim'
          variant='outlined'
          size='small'
          value={tag.value}
          onChange={(e) => setTag({ ...tag, value: e.target.value })}
          fullWidth
        />
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText }}
        >
          Kaydet
        </Button>
      </Paper>
    </Box>
  );
};
